import { FC } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { OneColored } from "@livechat/design-system-icons";
import {
  DesignToken,
  IPickerListItem,
  Icon,
  Link,
  Picker,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import { NOT_DETECTED_KEY } from "components/filters/language-filter/helpers";
import Flex from "components/layout/Flex";
import { HelpCenter } from "constants/help-center";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { shortPickerStyles } from "routes/automations/shared/styles";
import { selectLanguageLabels } from "store/entities/application-settings/selectors";
import { IRuleTriggerLanguage } from "store/entities/rules/interfaces";
import { withVar } from "themes/helpers";

import { changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerLanguage>;
}

const NOT_DETECTED_PICKER_OPTION: IPickerListItem = {
  key: NOT_DETECTED_KEY,
  name: "Not detected",
};

export const LanguageTrigger: FC<IProps> = ({ input }) => {
  const detectableLanguages = useSelector(selectLanguageLabels);

  const equalityOptions = [createEqualityOption("is"), createEqualityOption("isNot")];

  const selectedEqualityOptions = [createEqualityOption(input.value.invert ? "isNot" : "is")];

  const handleSelectEqualityOption = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    const selectedOption = selectedOptions[0];
    const newInvert = selectedOption.key === "isNot";
    changeTriggerValues(input, { invert: newInvert });
  };

  const languageOptions: IPickerListItem[] = [
    NOT_DETECTED_PICKER_OPTION,
    ...Object.entries(detectableLanguages).map(
      ([languageCode, languageLabel]): IPickerListItem => createLanguageOption(languageCode, languageLabel)
    ),
  ];

  const selectedLanguages = input.value.in.map(languageCode => {
    if (languageCode === NOT_DETECTED_KEY) {
      return NOT_DETECTED_PICKER_OPTION;
    }

    return createLanguageOption(languageCode, detectableLanguages[languageCode]);
  });

  const handleSelectLanguages = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      changeTriggerValues(input, { in: [] });
      return;
    }

    const newLanguages = selectedOptions.map(option => option.key);
    changeTriggerValues(input, { in: newLanguages });
  };

  return (
    <>
      <Picker
        options={equalityOptions}
        selected={selectedEqualityOptions}
        onSelect={handleSelectEqualityOption}
        hideClearButton
        searchDisabled
        css={shortPickerStyles}
        data-testid="language-condition-invert"
      />
      <Picker
        type="multi"
        options={languageOptions}
        selected={selectedLanguages}
        onSelect={handleSelectLanguages}
        selectAllOptionText="Select all"
        placeholder="Choose one or more..."
        data-testid="language-condition-languages"
      />
      <LanguageConditionExplainerContent />
    </>
  );
};

const LanguageConditionExplainerContent: FC = () => {
  const handleLearnMore = (): void => {
    trackEngagement("Workflows: Language condition 'Learn more' clicked", EventPlace.Automations);
  };

  return (
    <Flex gap={withVar(SpacingToken.Spacing2)} alignItems="flex-start">
      <Icon source={OneColored} size="small" css={{ marginTop: withVar(SpacingToken.Spacing05) }} />
      <Text customColor={withVar(DesignToken.ContentBasicSecondary)} size="sm">
        Language is detected by Text Copilot based only on the first message in the ticket.{" "}
        <Link href={HelpCenter.LANGUAGE_DETECTION} target="_blank" bold={false} onClick={handleLearnMore}>
          Learn more
        </Link>
      </Text>
    </Flex>
  );
};

const createEqualityOption = (key: "isNot" | "is"): IPickerListItem => ({
  key,
  name: key === "isNot" ? "is not" : "is",
});

const createLanguageOption = (languageCode: string, languageLabel: string): IPickerListItem => ({
  key: languageCode,
  name: `${languageLabel} (${languageCode.toLocaleUpperCase()})`,
});
